import { useState } from 'react';
import { hasAuthParams } from 'react-oidc-context';
import { useEffectOnce } from 'react-use';

import { useUserAuth } from '@bedc/client/auth/hooks';
import { createAuthSessionKey, getOidcUserManager, refreshSession } from '@bedc/client/auth/utils';
import { useBrand } from '@bedc/client/branding/hooks';
import { Button } from '@bedc/client/components/controls/button/Button';
import { LoadingSpinner } from '@bedc/client/components/controls/misc/LoadingSpinner';
import { clientConstants } from '@bedc/client/constants';

export const LoginPage = () => {
	// Hooks
	// ------------------------------------------------------------------------

	/** Brand. */
	const { brand } = useBrand();

	/** User auth. */
	const { login, isLoading, isAuthenticated } = useUserAuth();

	/** Store the redirect URL from query params. */
	const [redirectTo, setRedirectTo] = useState('');

	/** Whether resuming a stored session failed, so the user must sign in again. */
	const [resumeFailed, setResumeFailed] = useState(false);

	// Effects
	// ------------------------------------------------------------------------

	/** Get redirect URL from query params and clean up URL. */
	useEffectOnce(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const redirect = urlParams.get(clientConstants.auth.RedirectToParam);

		if (redirect) {
			setRedirectTo(redirect);

			// Remove the query param from URL without page reload
			urlParams.delete(clientConstants.auth.RedirectToParam);
			const newUrl = urlParams.toString()
				? `${window.location.pathname}?${urlParams.toString()}`
				: window.location.pathname;

			window.history.replaceState({}, '', newUrl);
		}
	});

	// Properties
	// ------------------------------------------------------------------------

	/**
	 * A returning user whose access token has expired is sent here by the edge. Try their refresh
	 * cookie before asking them to sign in again. The page cannot see that cookie, since it is
	 * HttpOnly, so it asks the refresh endpoint once. That renews the edge cookie, and the auth
	 * provider sends the user back once it sees them.
	 */
	const isResumingSession = !isLoading && !isAuthenticated && !hasAuthParams() && !resumeFailed;

	// Methods
	// ------------------------------------------------------------------------

	const resumeSession = async () => {
		if (!(await refreshSession())) {
			setResumeFailed(true);
			return;
		}

		// `refreshSession` returns early, without notifying the auth context, when the page already
		// holds a fresh user. Hand that user to the context so it redirects.
		const userManager = getOidcUserManager();
		const refreshedUser = await userManager.getUser();

		if (refreshedUser && !refreshedUser.expired) {
			await userManager.events.load(refreshedUser);
		} else {
			setResumeFailed(true);
		}
	};

	// Template
	// ------------------------------------------------------------------------

	// Show loading spinner while:
	// - OIDC is loading/processing callback
	// - A stored session is being resumed
	// - User is already authenticated (redirect will happen)
	if (isLoading || isAuthenticated || isResumingSession) {
		return (
			<>
				{isResumingSession && <SessionResumeLayer run={resumeSession} />}
				<LoadingSpinner />
			</>
		);
	}

	return (
		<div className="fixed inset-0 bg-surface-200 flex flex-col items-center justify-center">
			<div className="bg-white rounded-xs p-6">
				<img className="mb-5 w-[20rem]" alt={brand.logoAlt} src={brand.loginLogo} />
				<Button
					label="Login"
					className="w-full"
					onClick={() => {
						// Define a session ID so we can tie a session to a redirect
						const sessionId = Date.now().toString();

						if (redirectTo) {
							sessionStorage.setItem(createAuthSessionKey(sessionId), redirectTo);
						}

						login({
							extraTokenParams: { email: true },
							state: { sessionId },
						});
					}}
				/>
			</div>
		</div>
	);
};

/** Resumes the stored session once, post-commit. */
const SessionResumeLayer = ({ run }: { run: () => Promise<void> }): null => {
	useEffectOnce(() => {
		void run();
	});

	return null;
};
